
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface CategoryPreference {
  id: string;
  user_id: string;
  category: string;
  created_at: string;
}

export const AVAILABLE_CATEGORIES = [
  { key: "events", name_en: "Events", name_ar: "الفعاليات" },
  { key: "seasons", name_en: "Saudi Seasons", name_ar: "المواسم السعودية" },
  { key: "festivals", name_en: "Saudi Festivals", name_ar: "المهرجانات السعودية" },
  { key: "exhibitions", name_en: "Exhibitions", name_ar: "المعارض" },
  { key: "conferences-seminars", name_en: "Conferences & Seminars", name_ar: "المؤتمرات والندوات" },
  { key: "x-spaces", name_en: "X Spaces", name_ar: "مساحات X" },
  { key: "tiktok-lives", name_en: "TikTok Lives", name_ar: "بثوث تيك توك" },
];

export const useCategoryPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<CategoryPreference[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPreferences = useCallback(async () => {
    if (!user) {
      setPreferences([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("user_category_preferences")
      .select("*")
      .eq("user_id", user.id);

    if (error) {
      console.error("Error fetching preferences:", error);
    } else {
      setPreferences(data as CategoryPreference[]);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const toggleCategory = async (category: string, isRTL: boolean) => {
    if (!user) return;

    const existing = preferences.find((p) => p.category === category);

    if (existing) {
      // Remove preference
      const { error } = await supabase
        .from("user_category_preferences")
        .delete()
        .eq("id", existing.id);

      if (error) {
        toast.error(isRTL ? "حدث خطأ" : "An error occurred");
      } else {
        setPreferences((prev) => prev.filter((p) => p.id !== existing.id));
        toast.success(isRTL ? "تم إلغاء الاشتراك في هذه الفئة" : "Unsubscribed from this category");
      }
    } else {
      // Add preference
      const { data, error } = await supabase
        .from("user_category_preferences")
        .insert({ user_id: user.id, category })
        .select()
        .single();

      if (error) {
        toast.error(isRTL ? "حدث خطأ" : "An error occurred");
      } else {
        setPreferences((prev) => [...prev, data as CategoryPreference]);
        toast.success(isRTL ? "تم الاشتراك في هذه الفئة" : "Subscribed to this category");
      }
    }
  };

  const isSubscribed = (category: string) => {
    return preferences.some((p) => p.category === category);
  };

  return {
    preferences,
    loading,
    toggleCategory,
    isSubscribed,
    refetch: fetchPreferences,
  };
};
